import { FC, Ref, useImperativeHandle, useMemo, useRef, useState } from "react";
import Dialog from "../Dialog/Dialog";
import dialogCss from "../../common-styling/Dialog.module.css";
import { Services, useServices } from "../../services/Services";
import { Osdk } from "@osdk/client";
import { Event, User } from "@unruly-osdk/sdk";
import css from "./ShareDialog.module.css";
import { auth } from "../../auth/client";
import RadioField from "../Dialog/RadioField";
import { Public, People } from "@mui/icons-material";
import { DialogHandle } from "../Dialog/DialogTypes";
import { Visibility } from "@unruly-global/api-types";
import { isInternalEvent } from "../../helper/helper";

interface GenerateStepProps {
  event: Osdk.Instance<Event>;
  yourTake: string;
  setYourTake: (value: string) => void;
  visibility: Visibility;
  setVisibility: (value: Visibility) => void;
}

const GenerateStepContent: FC<GenerateStepProps> = ({
  event,
  yourTake,
  setYourTake,
  visibility,
  setVisibility,
}) => {
  return (
    <>
      <div className={dialogCss.label}>Event</div>
      <div className={dialogCss.field}>
        <input type="text" disabled={true} defaultValue={event.$title ?? ""} />
      </div>

      <div className={dialogCss.label}>Your Take</div>
      <div className={dialogCss.field}>
        <textarea
          name={"your-take-" + event.id}
          autoFocus
          placeholder="Add your insight or perspective on this content"
          value={yourTake}
          onChange={(e) => setYourTake(e.target.value)}
          rows={10}
          maxLength={3000}
        />
      </div>
      <div className={dialogCss["field-group"]}>
        <div className={dialogCss.label}>Share with</div>
        <RadioField
          name={"visibility-" + event.id}
          options={[
            { label: "Everyone", value: "PUBLIC" as const },
            { label: "Connections only", value: "CONNECTIONS" as const },
          ]}
          selectedValue={visibility}
          setSelectedValue={setVisibility}
        />
      </div>
    </>
  );
};

interface PreviewStepProps {
  user: Osdk.Instance<User>;
  yourTake: string;
  visibility: Visibility;
  event: Osdk.Instance<Event>;
}
const PreviewStepContent: FC<PreviewStepProps> = ({
  user,
  yourTake,
  visibility,
  event,
}) => {
  const services = useServices();
  const { data: employer } = services.User.useGetEmployer(user ?? undefined);
  const isInternalEventEvent = isInternalEvent(event.sourceType);
  const eventLink = isInternalEventEvent
    ? `${location.origin}/event/${event.id}`
    : (event.newsSourceUrl ?? "");
  let linkDomain = "";
  try {
    linkDomain = eventLink ? new URL(eventLink).hostname : "";
  } catch (error) {
    console.warn("Invalid event link for share preview", error);
  }
  return (
    <div>
      <div className={css.linkedinHeader}>
        <h2 className={css.userName}>
          {user?.fullName} · <span className={css.headerSubText}>You</span>
        </h2>
        <div className={css.headerSubText}>
          {user?.organizationRole} at {employer?.$title}
        </div>
        <div className={css.headerSubText}>
          Now <span className={css.centerDot}>·</span>{" "}
          {visibility === "PUBLIC" ? <Public /> : <People />}
        </div>
      </div>
      <p className={css.yourTake}>{yourTake}</p>
      <div className={css.linkBox}>
        <div className={css.linkTitle}>{event.name || event.$title}</div>
        <div className={css.linkSourceDomain}>
          {linkDomain || location.hostname}
        </div>
      </div>
    </div>
  );
};

async function shareToLinkedIn(
  eventId: string,
  visibility: Visibility,
  yourTake: string,
  user: Osdk.Instance<User>,
  services: Services,
  postId: { value: string | null },
) {
  const returnUrl =
    location.pathname +
    "?" +
    new URLSearchParams({ eventId, visibility, yourTake }).toString();
  // We don't set the postId here because we don't have it yet. It will be set in onClose after the
  // share is successful.
  const response = await services.User.sharePostOnLinkedIn(
    user.id,
    eventId,
    yourTake,
    visibility,
    auth.getTokenOrUndefined()!,
    returnUrl.toString(),
  );
  if ("authorizationRequired" in response) {
    location.href = response.authorizationRequired.authorizationUrl;
  } else {
    postId.value = response.success.postId;
  }
}
interface ShareDialogProps {
  onShareSuccess?: (postId: string) => void;
  ref: Ref<ShareDialogHandle>;
}

export interface ShareDialogHandle {
  show: (eventId: string) => void;
  hide: () => void;
}

export const ShareDialog: FC<ShareDialogProps> = ({ onShareSuccess, ref }) => {
  const [eventId, setEventId] = useState<string | undefined>();
  const [step, setStep] = useState<"generate" | "preview">("generate");
  const [yourTake, setYourTake] = useState<string>("");
  const [visibility, setVisibility] = useState<Visibility>("PUBLIC");
  // This is a hacky way to share the postId between onSumbit and onClose. They both run in the same
  // render, so we can use a mutable object to hold the value.
  // We can't use state because onClose runs after the dialog is closed, which is after the component is
  // unmounted, so state would be lost.
  const postId: { value: string | null } = useMemo(
    () => ({
      value: null,
    }),
    [],
  );
  const services = useServices();
  const { data: event } = services.Event.useGetEventById(eventId);
  const { data: user } = services.User.useGetCurrent();
  const dialogRef = useRef<DialogHandle>(null);
  useImperativeHandle(ref, () => ({
    show: (newEventId) => {
      setStep("generate");
      if (eventId !== newEventId) {
        setYourTake("");
        setVisibility("PUBLIC");
      }
      setEventId(newEventId);
      dialogRef.current?.show();
    },
    hide: () => {
      dialogRef.current?.hide();
    },
  }));

  async function onSubmit(data: FormData) {
    if (step === "generate") {
      setStep("preview");
      setYourTake(data.get("your-take-" + eventId) as string);
      setVisibility(data.get("visibility-" + eventId) as Visibility);
      return true;
    } else if (step === "preview") {
      await shareToLinkedIn(
        eventId!,
        visibility,
        yourTake,
        user!,
        services,
        postId,
      );
      return false;
    } else {
      // This can never happen if we've handled all the cases above.
      const impossibleStep: never = step;
      throw new Error("Invalid step in ShareDialog: " + impossibleStep);
    }
  }
  const action = step === "generate" ? "Generate Draft" : "Post to LinkedIn";
  function onError(error: unknown) {
    console.error(error);
    return "Something went wrong sharing the event. If this persists, you can contact Unruly support through accounts@unrulycorp.com";
  }
  function onClose() {
    if (postId.value) {
      onShareSuccess?.(postId.value);
    }
  }
  return (
    <Dialog
      title="Share to LinkedIn"
      action={action}
      onSubmit={onSubmit}
      onError={onError}
      onClose={onClose}
      ref={dialogRef}
    >
      {!event || (step === "preview" && !user) ? (
        "Loading..."
      ) : step === "generate" ? (
        <GenerateStepContent
          event={event}
          yourTake={yourTake}
          setYourTake={setYourTake}
          visibility={visibility}
          setVisibility={setVisibility}
        />
      ) : step === "preview" ? (
        <PreviewStepContent
          yourTake={yourTake}
          visibility={visibility}
          event={event}
          user={user!}
        />
      ) : null}
    </Dialog>
  );
};
